// src/services/reportsService.ts
import { gridsService } from './gridsService';
import { staffService } from './staffService';
import { intentionsService } from './intentionsService';
import { TeachingIntention } from '../types/intentions';
import { StaffAllocationGrid } from '../types/grid';
import { resolveClassMinutes } from '../utils/classMinutes';
import {
  displayLearningArea,
  normalizeLearningArea,
  learningAreasFromStaffFields,
  CANONICAL_LEARNING_AREA_NAMES,
} from '../constants/learningAreas';

/**
 * Service for aggregating data across different sources for reports
 */
export const reportsService = {
  /**
   * Get teacher assignments from grid data
   * @param teacherId The teacher ID or name to look for
   * @param gridId Optional specific grid ID, otherwise uses all active grids
   */
  async getTeacherAssignments(teacherId: string, gridId?: string) {
    try {
      let grids: StaffAllocationGrid[];
      
      if (gridId) {
        const grid = await gridsService.getGridById(gridId);
        grids = grid ? [grid] : [];
      } else {
        grids = await gridsService.getAllGrids();
        // Filter to active grids only
        grids = grids.filter(g => g.isActive);
      }

      const assignments: Array<{
        gridId: string;
        gridName: string;
        classId: string;
        subjectCode: string;
        className: string;
        curriculumArea: string;
        yearLevel: number;
        lineId: string;
        minutes: number;
      }> = [];

      grids.forEach(grid => {
        (grid.classes || []).forEach(cls => {
          // Check if teacher is assigned to any line in this class
          const assignedToClass = Object.entries(cls.lineAssignments || {}).some(
            ([, assignedTeacher]) => 
              assignedTeacher && (assignedTeacher === teacherId || assignedTeacher.includes(teacherId))
          );

          if (assignedToClass) {
            // Find which line(s) the teacher is assigned to
            Object.entries(cls.lineAssignments || {}).forEach(([lineId, assignedTeacher]) => {
              if (assignedTeacher && (assignedTeacher === teacherId || assignedTeacher.includes(teacherId))) {
                assignments.push({
                  gridId: grid.id,
                  gridName: grid.name,
                  classId: cls.id,
                  subjectCode: cls.subjectCode || '',
                  className: cls.className,
                  curriculumArea: cls.curriculumArea || '',
                  yearLevel: cls.yearLevel || 0,
                  lineId: lineId,
                  minutes: resolveClassMinutes(cls, grid, 60)
                });
              }
            });
          }
        });
      });

      return assignments;
    } catch (error) {
      console.error('Error getting teacher assignments:', error);
      return [];
    }
  },

  /**
   * Calculate required FTE for a subject area based on grid classes
   * @param subjectArea The subject area
   * @param grids Optional specific grids, otherwise fetches all active grids
   */
  async calculateRequiredFTE(subjectArea: string, grids?: StaffAllocationGrid[]) {
    try {
      if (!grids) {
        grids = await gridsService.getAllGrids();
        grids = grids.filter(g => g.isActive);
      }

      let totalMinutes = 0;
      let classCount = 0;

      grids.forEach(grid => {
        (grid.classes || []).forEach(cls => {
          if (cls.curriculumArea === subjectArea) {
            const minutes = resolveClassMinutes(cls, grid, 60);
            // Each class is counted once per line assignment
            const lineCount = Object.keys(cls.lineAssignments || {}).length;
            totalMinutes += minutes * (lineCount || 1);
            classCount++;
          }
        });
      });

      // Convert to FTE (assuming 1200 minutes per week = 1.0 FTE)
      const requiredFTE = totalMinutes / 1200;

      return {
        requiredFTE,
        totalMinutes,
        classCount
      };
    } catch (error) {
      console.error('Error calculating required FTE:', error);
      return {
        requiredFTE: 0,
        totalMinutes: 0,
        classCount: 0
      };
    }
  },

  /**
   * Match teacher preferences to their actual assignments
   * @param preferences Teaching preferences from intentions
   * @param assignments Actual assignments from grid
   */
  matchPreferences(
    preferences: TeachingIntention['teachingPreferences'],
    assignments: Array<{ curriculumArea: string; yearLevel: number; specificSubject?: string }>
  ) {
    if (!preferences || preferences.length === 0) {
      return {
        totalPreferences: 0,
        satisfiedCount: 0,
        partialCount: 0,
        unsatisfiedCount: 0,
        satisfactionScore: 0,
        details: []
      };
    }

    const details: Array<{
      learningArea: string;
      specificSubject: string;
      rank: number;
      satisfied: boolean | 'partial';
      assignedSubjects: string[];
    }> = [];

    let satisfiedCount = 0;
    let partialCount = 0;
    let unsatisfiedCount = 0;
    let weightedScore = 0;
    let totalWeight = 0;

    preferences.forEach(pref => {
      const weight = 6 - pref.rank; // Rank 1 = weight 5, rank 5 = weight 1
      totalWeight += weight;

      // Check if any assignment matches this preference
      const matchingAssignments = assignments.filter(a => {
        const areaMatch = a.curriculumArea.toLowerCase().includes(pref.learningArea.toLowerCase()) ||
                         pref.learningArea.toLowerCase().includes(a.curriculumArea.toLowerCase());
        
        // If specific subject is provided, check for more precise match
        const specificMatch = pref.specificSubject && a.specificSubject ?
          a.specificSubject.toLowerCase().includes(pref.specificSubject.toLowerCase()) ||
          pref.specificSubject.toLowerCase().includes(a.specificSubject.toLowerCase()) :
          true;
        
        return areaMatch && specificMatch;
      });

      let satisfied: boolean | 'partial' = false;
      if (matchingAssignments.length > 0) {
        // Full match if we have assignments matching this preference
        satisfied = true;
        satisfiedCount++;
        weightedScore += weight;
      } else {
        // Check for partial match (area match only)
        const areaOnlyMatch = assignments.some(a => 
          a.curriculumArea.toLowerCase().includes(pref.learningArea.toLowerCase()) ||
          pref.learningArea.toLowerCase().includes(a.curriculumArea.toLowerCase())
        );

        if (areaOnlyMatch) {
          satisfied = 'partial';
          partialCount++;
          weightedScore += weight * 0.5;
        } else {
          unsatisfiedCount++;
        }
      }

      details.push({
        learningArea: pref.learningArea,
        specificSubject: pref.specificSubject,
        rank: pref.rank,
        satisfied,
        assignedSubjects: matchingAssignments.map(a => `${a.curriculumArea}${a.yearLevel ? ' Y' + a.yearLevel : ''}`)
      });
    });

    const satisfactionScore = totalWeight > 0 ? Math.round((weightedScore / totalWeight) * 100) : 0;

    return {
      totalPreferences: preferences.length,
      satisfiedCount,
      partialCount,
      unsatisfiedCount,
      satisfactionScore,
      details
    };
  },

  /**
   * Calculate workload for a teacher based on grid assignments
   * @param teacherId Teacher ID or name
   * @param targetFTE Target FTE from staff record
   * @param polMinutes POL allowance minutes
   * @param otherAllowances Other allowance minutes
   */
  async calculateWorkload(
    teacherId: string,
    targetFTE: number,
    polMinutes: number = 0,
    otherAllowances: number = 0
  ) {
    try {
      const assignments = await this.getTeacherAssignments(teacherId);
      
      const totalMinutes = assignments.reduce((sum, a) => sum + a.minutes, 0);
      const targetMinutes = targetFTE * 1200; // 1.0 FTE = 1200 minutes
      const availableMinutes = targetMinutes - polMinutes - otherAllowances;
      const utilization = availableMinutes > 0 ? Math.round((totalMinutes / availableMinutes) * 100) : 0;

      return {
        targetMinutes,
        allocatedMinutes: totalMinutes,
        polMinutes,
        otherAllowances,
        availableMinutes,
        utilization,
        classCount: assignments.length,
        classes: assignments
      };
    } catch (error) {
      console.error('Error calculating workload:', error);
      return {
        targetMinutes: 0,
        allocatedMinutes: 0,
        polMinutes: 0,
        otherAllowances: 0,
        availableMinutes: 0,
        utilization: 0,
        classCount: 0,
        classes: []
      };
    }
  },

  /**
   * Get all subject areas from grids
   */
  async getSubjectAreas(): Promise<string[]> {
    try {
      const grids = await gridsService.getAllGrids();
      const areas = new Set<string>();

      grids.forEach(grid => {
        (grid.classes || []).forEach(cls => {
          if (cls.curriculumArea) {
            // Collapse mixed casing / slash-joined values onto one canonical
            // learning-area label so the list matches every other surface.
            areas.add(displayLearningArea(cls.curriculumArea));
          }
        });
      });

      // Canonical learning areas first (in configured order), then any extras.
      const canonical = CANONICAL_LEARNING_AREA_NAMES.filter(name => areas.has(name));
      const extras = Array.from(areas)
        .filter(name => !CANONICAL_LEARNING_AREA_NAMES.includes(name))
        .sort();
      return [...canonical, ...extras];
    } catch (error) {
      console.error('Error getting subject areas:', error);
      return [];
    }
  },

  /**
   * Get qualified staff for a subject area
   * @param subjectArea The subject area
   * @param year The academic year for intentions
   */
  async getQualifiedStaff(subjectArea: string, year: number) {
    try {
      const [allStaff, intentions] = await Promise.all([
        staffService.getActiveStaff(),
        intentionsService.getIntentionsByYear(year)
      ]);

      // Create a map of staff with their intentions
      const intentionsMap = new Map(
        intentions.map(i => [i.staffId, i])
      );

      const targetArea = normalizeLearningArea(subjectArea) || subjectArea;
      const qualifiedStaff = allStaff.filter(staff => {
        // Match on the staff member's canonical learning area(s), or a preference.
        const staffAreas = learningAreasFromStaffFields(staff.department, staff.curriculum_area);
        const curriculumMatch = staffAreas.includes(targetArea);

        const intention = intentionsMap.get(staff.staff_id);
        const preferenceMatch = intention?.teachingPreferences?.some(
          p => normalizeLearningArea(p.learningArea) === targetArea
        );

        return curriculumMatch || preferenceMatch;
      }).map(staff => {
        const intention = intentionsMap.get(staff.staff_id);
        return {
          ...staff,
          intention,
          isReturning: intention?.intentionToReturn === 'yes',
          isLeaving: intention?.intentionToReturn === 'no',
          isUncertain: intention?.intentionToReturn === 'uncertain'
        };
      });

      return qualifiedStaff;
    } catch (error) {
      console.error('Error getting qualified staff:', error);
      return [];
    }
  },

  /**
   * Calculate subject demand from grid data
   */
  async calculateSubjectDemand() {
    try {
      const grids = await gridsService.getAllGrids();
      const activeGrids = grids.filter(g => g.isActive);

      const demandMap = new Map<string, {
        subjectCode: string;
        subjectName: string;
        department: string;
        totalClasses: number;
        totalMinutes: number;
        teachersRequired: number;
      }>();

      activeGrids.forEach(grid => {
        (grid.classes || []).forEach(cls => {
          const key = cls.subjectCode || cls.className;
          const minutes = resolveClassMinutes(cls, grid, 60);
          const lineCount = Object.keys(cls.lineAssignments || {}).length || 1;

          if (demandMap.has(key)) {
            const existing = demandMap.get(key)!;
            existing.totalClasses += 1;
            existing.totalMinutes += minutes * lineCount;
            existing.teachersRequired = existing.totalMinutes / 1200;
          } else {
            demandMap.set(key, {
              subjectCode: cls.subjectCode || key,
              subjectName: cls.className,
              department: displayLearningArea(cls.curriculumArea),
              totalClasses: 1,
              totalMinutes: minutes * lineCount,
              teachersRequired: (minutes * lineCount) / 1200
            });
          }
        });
      });

      return Array.from(demandMap.values());
    } catch (error) {
      console.error('Error calculating subject demand:', error);
      return [];
    }
  },

  /**
   * Calculate staffing gaps by comparing demand with available qualified staff
   * @param year The academic year for intentions
   */
  async calculateStaffingGaps(year: number) {
    try {
      const [demand, allStaff, intentions] = await Promise.all([
        this.calculateSubjectDemand(),
        staffService.getActiveStaff(),
        intentionsService.getIntentionsByYear(year)
      ]);

      // Create intentions map
      const intentionsMap = new Map(
        intentions.map(i => [i.staffId, i])
      );

      const gaps = demand.map(subjectDemand => {
        // Find qualified staff for this subject
        const qualifiedStaff = allStaff.filter(staff => {
          const staffAreas = learningAreasFromStaffFields(staff.department, staff.curriculum_area);
          const curriculumMatch = staffAreas.includes(subjectDemand.department);
          const intention = intentionsMap.get(staff.staff_id);
          const preferenceMatch = intention?.teachingPreferences?.some(
            p => normalizeLearningArea(p.learningArea) === subjectDemand.department
          );
          return curriculumMatch || preferenceMatch;
        });

        // Calculate available FTE
        let availableFTE = 0;
        const staffDetails = qualifiedStaff.map(staff => {
          const intention = intentionsMap.get(staff.staff_id);
          const isLeaving = intention?.intentionToReturn === 'no';
          const staffFTE = isLeaving ? 0 : (staff.fte || 1.0);
          
          // Estimate available FTE (simplified - assumes even distribution across subjects)
          const availableForSubject = staffFTE / (intention?.teachingPreferences?.length || 1);
          availableFTE += availableForSubject;

          return {
            teacherId: staff.staff_id,
            teacherName: staff.name,
            teacherCode: staff.staff_id || staff.name.substring(0, 3).toUpperCase(),
            status: isLeaving ? 'leaving' as const : 'current' as const,
            fte: staffFTE,
            utilizationPercent: 100,
            availableFTE: availableForSubject
          };
        });

        // Calculate gap
        const gapFTE = Math.max(0, subjectDemand.teachersRequired - availableFTE);
        
        // Determine severity
        let gapSeverity: 'critical' | 'high' | 'medium' | 'low' | 'none' = 'none';
        if (gapFTE > 0) {
          const gapPercentage = (gapFTE / subjectDemand.teachersRequired) * 100;
          if (gapPercentage >= 50) gapSeverity = 'critical';
          else if (gapPercentage >= 25) gapSeverity = 'high';
          else if (gapPercentage >= 10) gapSeverity = 'medium';
          else gapSeverity = 'low';
        }

        return {
          subjectCode: subjectDemand.subjectCode,
          subjectName: subjectDemand.subjectName,
          department: subjectDemand.department,
          yearLevels: ['7-10', '11-12'], // Simplified - would need more data to determine
          teachersRequired: Math.round(subjectDemand.teachersRequired * 100) / 100,
          qualifiedTeachers: qualifiedStaff.length,
          gapFTE: Math.round(gapFTE * 100) / 100,
          gapSeverity,
          qualifiedStaff: staffDetails
        };
      });

      return gaps.filter(gap => gap.teachersRequired > 0);
    } catch (error) {
      console.error('Error calculating staffing gaps:', error);
      return [];
    }
  },

  /**
   * Calculate department summaries from staffing gaps
   */
  async calculateDepartmentSummaries(year: number) {
    try {
      const [gaps, allStaff, intentions] = await Promise.all([
        this.calculateStaffingGaps(year),
        staffService.getActiveStaff(),
        intentionsService.getIntentionsByYear(year)
      ]);

      const intentionsMap = new Map(intentions.map(i => [i.staffId, i]));
      const departmentMap = new Map<string, {
        department: string;
        currentFTE: number;
        requiredFTE: number;
        gapFTE: number;
        teacherCount: number;
        leavingCount: number;
        subjects: Array<{
          subjectCode: string;
          subjectName: string;
          gapFTE: number;
        }>;
      }>();

      // Initialize departments from gaps
      gaps.forEach(gap => {
        if (!departmentMap.has(gap.department)) {
          departmentMap.set(gap.department, {
            department: gap.department,
            currentFTE: 0,
            requiredFTE: 0,
            gapFTE: 0,
            teacherCount: 0,
            leavingCount: 0,
            subjects: []
          });
        }

        const dept = departmentMap.get(gap.department)!;
        dept.requiredFTE += gap.teachersRequired;
        dept.gapFTE += gap.gapFTE > 0 ? gap.gapFTE : 0;
        dept.subjects.push({
          subjectCode: gap.subjectCode,
          subjectName: gap.subjectName,
          gapFTE: gap.gapFTE > 0 ? gap.gapFTE : 0
        });
      });

      // Add staff data to departments, bucketing each staff member into every
      // canonical learning area their record references (multi-area aware).
      allStaff.forEach(staff => {
        const staffAreas = learningAreasFromStaffFields(staff.department, staff.curriculum_area);
        const intention = intentionsMap.get(staff.staff_id);
        const leaving = intention?.intentionToReturn === 'no';

        staffAreas.forEach(areaName => {
          const dept = departmentMap.get(areaName);
          if (dept) {
            dept.currentFTE += staff.fte || 0;
            dept.teacherCount++;
            if (leaving) dept.leavingCount++;
          }
        });
      });

      return Array.from(departmentMap.values());
    } catch (error) {
      console.error('Error calculating department summaries:', error);
      return [];
    }
  }
};
